import { Injectable } from '@angular/core';

interface CredentialDescriptorJson {
  type: string;
  id: string;
  transports?: string[];
}

export interface CreationOptionsJson {
  rp: { id?: string; name: string };
  user: { id: string; name: string; displayName: string };
  challenge: string;
  pubKeyCredParams: { type: string; alg: number }[];
  timeout?: number;
  attestation?: string;
  authenticatorSelection?: {
    authenticatorAttachment?: string;
    residentKey?: string;
    requireResidentKey?: boolean;
    userVerification?: string;
  };
  excludeCredentials?: CredentialDescriptorJson[];
}

export interface RequestOptionsJson {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: CredentialDescriptorJson[];
  userVerification?: string;
}

export interface PasskeyRegistration {
  attestationResponseJson: string;
  authenticatorAttachment: string | null;
}

const KNOWN_TRANSPORTS: AuthenticatorTransport[] = ['usb', 'nfc', 'ble', 'internal', 'hybrid'];
export const PASSKEY_CANCELLED_MESSAGE = 'Passkey request was cancelled.';
export const PASSKEY_EXISTS_MESSAGE =
  'You already have a passkey on this device. Add one from another device or a security key instead.';
export const PASSKEY_GENERIC_ERROR = 'Something went wrong with your passkey. Please try again.';

export function base64UrlToBytes(value: string): ArrayBuffer {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export function bytesToBase64Url(buffer: ArrayBuffer): string {
  let binary = '';
  for (const byte of new Uint8Array(buffer)) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function toDescriptor(descriptor: CredentialDescriptorJson): PublicKeyCredentialDescriptor {
  const transports = (descriptor.transports ?? []).filter((t): t is AuthenticatorTransport =>
    KNOWN_TRANSPORTS.includes(t as AuthenticatorTransport)
  );
  return { type: 'public-key', id: base64UrlToBytes(descriptor.id), transports };
}

export function toCreationOptions(json: CreationOptionsJson): PublicKeyCredentialCreationOptions {
  return {
    rp: json.rp,
    user: {
      id: base64UrlToBytes(json.user.id),
      name: json.user.name,
      displayName: json.user.displayName,
    },
    challenge: base64UrlToBytes(json.challenge),
    pubKeyCredParams: json.pubKeyCredParams.map((p) => ({ type: 'public-key' as const, alg: p.alg })),
    timeout: json.timeout,
    attestation: json.attestation as AttestationConveyancePreference | undefined,
    authenticatorSelection: json.authenticatorSelection as AuthenticatorSelectionCriteria | undefined,
    excludeCredentials: (json.excludeCredentials ?? []).map(toDescriptor),
  };
}

export function toRequestOptions(json: RequestOptionsJson): PublicKeyCredentialRequestOptions {
  return {
    challenge: base64UrlToBytes(json.challenge),
    timeout: json.timeout,
    rpId: json.rpId,
    allowCredentials: (json.allowCredentials ?? []).map(toDescriptor),
    userVerification: json.userVerification as UserVerificationRequirement | undefined,
  };
}

export function serializeAttestation(credential: PublicKeyCredential): string {
  const response = credential.response as AuthenticatorAttestationResponse;
  const transports = typeof response.getTransports === 'function'
    ? response.getTransports().filter((t) => KNOWN_TRANSPORTS.includes(t as AuthenticatorTransport))
    : [];
  return JSON.stringify({
    id: credential.id,
    rawId: bytesToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: bytesToBase64Url(response.attestationObject),
      clientDataJSON: bytesToBase64Url(response.clientDataJSON),
      transports,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  });
}

export function serializeAssertion(credential: PublicKeyCredential): string {
  const response = credential.response as AuthenticatorAssertionResponse;
  return JSON.stringify({
    id: credential.id,
    rawId: bytesToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: bytesToBase64Url(response.authenticatorData),
      clientDataJSON: bytesToBase64Url(response.clientDataJSON),
      signature: bytesToBase64Url(response.signature),
      ...(response.userHandle ? { userHandle: bytesToBase64Url(response.userHandle) } : {}),
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  });
}

/** Maps WebAuthn failures to something a user can act on; never leaks internals. */
export function describePasskeyError(error: unknown): string {
  if (error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'AbortError')) {
    return PASSKEY_CANCELLED_MESSAGE;
  }
  // The authenticator already holds a passkey for this account: excludeCredentials made it refuse. Nothing is
  // broken, so say what happened rather than sending the user round the same loop again.
  if (error instanceof DOMException && error.name === 'InvalidStateError') {
    return PASSKEY_EXISTS_MESSAGE;
  }
  if (error instanceof Error && error.message && !(error instanceof DOMException)) {
    return error.message;
  }
  return PASSKEY_GENERIC_ERROR;
}

@Injectable({
  providedIn: 'root',
})
export class PasskeyService {
  isSupported(): boolean {
    return typeof window !== 'undefined' && 'PublicKeyCredential' in window && !!navigator.credentials;
  }

  async register(optionsJson: string): Promise<PasskeyRegistration> {
    const publicKey = toCreationOptions(JSON.parse(optionsJson) as CreationOptionsJson);
    const credential = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
    if (!credential) {
      throw new Error(PASSKEY_CANCELLED_MESSAGE);
    }
    return {
      attestationResponseJson: serializeAttestation(credential),
      authenticatorAttachment: credential.authenticatorAttachment ?? null,
    };
  }

  async authenticate(optionsJson: string): Promise<string> {
    return this.getAssertion(optionsJson, {});
  }

  /** Browser autofill ("conditional UI"): resolves only if the user picks a passkey from the username field's suggestions. */
  async isConditionalMediationAvailable(): Promise<boolean> {
    if (!this.isSupported() || typeof PublicKeyCredential.isConditionalMediationAvailable !== 'function') {
      return false;
    }
    try {
      return await PublicKeyCredential.isConditionalMediationAvailable();
    } catch {
      return false;
    }
  }

  async authenticateConditional(optionsJson: string, signal: AbortSignal): Promise<string> {
    return this.getAssertion(optionsJson, { mediation: 'conditional', signal });
  }

  private async getAssertion(optionsJson: string, extra: Pick<CredentialRequestOptions, 'mediation' | 'signal'>): Promise<string> {
    const publicKey = toRequestOptions(JSON.parse(optionsJson) as RequestOptionsJson);
    const credential = (await navigator.credentials.get({ publicKey, ...extra })) as PublicKeyCredential | null;
    if (!credential) {
      throw new Error(PASSKEY_CANCELLED_MESSAGE);
    }
    return serializeAssertion(credential);
  }
}
